// @ts-ignore
import { hexToRgb as _hexToRgb } from "@my-scoot/exly-react-component-lib/build/cjs/features/Common/modules/Colors/utils/Colors.utils";
import { hexToRgb as hexToRgbTypes } from "@my-scoot/exly-react-component-lib/build/types/features/Common/modules/Colors/utils/Colors.utils";
import {
    DEFAULT_DARK_TEXT_COLOR,
    DEFAULT_LIGHT_TEXT_COLOR,
    DEFAULT_TEXT_COLOR,
} from "../Colors.constants";

/**
 * This function checks for whether the rgb Color Obj falls under grayscale(black shaded lightness)
 * @param rgbColorObj => { r, g, b }
 * @param_summary
 *  r is red value in numbers 0-255,
 *  g is green value in numbers 0-255,
 *  b is blue value in numbers 0-255,
 * @returns boolean `isGrayScale` whether grayscale or not
 */
export const checkIfRgbColorObjIsGrayScale = ({
    r,
    g,
    b,
}: {
    r: number;
    g: number;
    b: number;
}) => {
    // Normalize values to be in the range [0, 1]
    const R = +r / 255;
    const G = +g / 255;
    const B = +b / 255;

    // Find the maximum and minimum values among R, G, and B
    const max = Math.max(R, G, B);
    const min = Math.min(R, G, B);
    const isGrayScale = max === min;

    return isGrayScale;
};

export const hexToRgb: typeof hexToRgbTypes = _hexToRgb;
/**
 * This function checks for if the rgb css string provided is grayscale or not
 * @param rgbCssString rgb css string
 * @returns `isGrayScale` boolean (grayscale or not)
 */
export const checkIfRgbCssStringIsGrayScale = (rgbCssString: string) => {
    const { isRgb, rgbColorObj } = getRgbCssStringColorData(rgbCssString);
    const { r, g, b } = rgbColorObj;
    if (!isRgb) return false;
    const isGrayScale = checkIfRgbColorObjIsGrayScale({
        r,
        g,
        b,
    });
    return isGrayScale;
};

/**
 * This function returns rgb color data
 * @param rgbCssString rgb string
 * @returns rgb color data
 * {
 *  isRgb: if rgb string provided is rgb or not using regex matching,
 *  rgbColorObj: { r,g,b }
 * }
 *  @summary
 *  where
 *  r: red value b/w 0-255
 *  g: red value b/w 0-255
 *  b: red value b/w 0-255
 */
export const getRgbCssStringColorData = (rgbCssString: string) => {
    const rgbRegexPattern = /rgb\((\d+),\s*(\d+),\s*(\d+)\)/;
    const match = (rgbCssString || "").match(rgbRegexPattern);
    const [, r, g, b] = match || [];
    return {
        isRgb: !!match,
        rgbColorObj: { r: +r, g: +g, b: +b },
    };
};

export const isLightColor = (color?: string) =>
    color === DEFAULT_LIGHT_TEXT_COLOR;

/**
 * This calculates the complimentary color based on the
 * percieved brightness of the rgb color of that section
 * percievedBrightness = (299R+587G+114B)/1000
 * if(percievedBrightness > 128) `darkColorHex` returned else `lightColorHex`
 * if not rgb string then `defaultColorHex` is returned
 * @param arg => { rgbCssString, lightColorHex, darkColorHex, defaultColorHex }
 * rgbCssString is rgb css string,
 * lightColorHex is  light text color for darker backgrounds in hex,
 * darkColorHex is dark text color for lighter backgrounds in hex,
 * defaultColorHex is default text color in hex,
 * @returns complimentary hex color string based on above calculation
 */
export const getComplimentaryHexColorFromRgbCssString = ({
    rgbCssString,
    lightColorHex = DEFAULT_LIGHT_TEXT_COLOR,
    darkColorHex = DEFAULT_DARK_TEXT_COLOR,
    defaultColorHex = DEFAULT_TEXT_COLOR,
}: {
    rgbCssString: string;
    lightColorHex?: string;
    darkColorHex?: string;
    defaultColorHex?: string;
}) => {
    const rgbString = hexToRgb(rgbCssString || "");
    const { isRgb, rgbColorObj } = getRgbCssStringColorData(rgbString);
    if (isRgb) {
        const { r, g, b } = rgbColorObj;
        const percievedBrightness = (299 * r + 587 * g + 114 * b) / 1000;
        if (percievedBrightness > 128) return darkColorHex;
        else return lightColorHex;
    } else return defaultColorHex;
};

/**
 * Converts a HEX or RGB color to RGBA with a given alpha value.
 * @param {string} colorValue - The input color in HEX (e.g. #FF5733 or #F53) or RGB (e.g. rgb(255, 87, 51)) format.
 * @param {number} alphaValue - The alpha value for the color (0-1 range).
 * @returns {string} The color in RGBA format.
 */
export const colorWithAlpha = (
    colorValue: string = "",
    alphaValue: number = 1
): string => {
    // Ensure alpha is within the range of 0 to 1
    const alpha = Math.max(0, Math.min(1, alphaValue));
    let color = colorValue;
    // Check if the color is in HEX format
    if (colorValue.startsWith("#")) {
        // Normalize HEX color to full 6 characters format
        if (colorValue.length === 4) {
            color = `#${color[1]}${color[1]}${color[2]}${
                color[2 + +color[3] + +color[3]]
            }`;
        } else if (colorValue.length !== 7) {
            throw new Error("Invalid HEX color format");
        }

        // Convert HEX to RGB
        const r = parseInt(color.substring(1, 3), 16);
        const g = parseInt(color.substring(3, 5), 16);
        const b = parseInt(color.substring(5, 7), 16);

        // Return the RGBA color
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    // Check if the color is in RGB format
    if (color.startsWith("rgb(")) {
        // Replace 'rgb' with 'rgba' and append the alpha value
        return color.replace("rgb", "rgba").replace(")", `, ${alpha})`);
    }

    // If the color format is neither HEX nor RGB, throw an error
    throw new Error("Invalid color format");
};
